<script setup>
// NOTICE: KAPTION-INTEGRATION - Updated to support Follow-up and Schedule modes for WhatsApp/Twilio
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // NOTICE: KAPTION-INTEGRATION - Whether follow-up mode is available (requires meta payment confirmed)
  /**
   * Whether follow-up mode is available (requires meta payment confirmed)
   */
  isFollowupAvailable: {
    type: Boolean,
    default: false,
  },
  // NOTICE: KAPTION-INTEGRATION - Whether schedule tab should be shown (WhatsApp/Twilio channels)
  /**
   * Whether schedule tab should be shown (for WhatsApp/Twilio channels)
   */
  showScheduleTab: {
    type: Boolean,
    default: false,
  },
  showPrivateNote: {
    type: Boolean,
    default: true,
  },
});

defineEmits(['setMode']);

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorScheduleMode = useTemplateRef('wootEditorScheduleMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');

const replyModeSize = useElementSize(wootEditorReplyMode);
const scheduleModeSize = useElementSize(wootEditorScheduleMode);
const privateModeSize = useElementSize(wootEditorPrivateMode);

/**
 * NOTICE: KAPTION-INTEGRATION
 * Determines if Follow-up mode should be shown instead of Reply
 * Follow-up mode is available when:
 * - WhatsApp channel AND
 * - can_reply is false (24h window expired) AND
 * - Meta payment is confirmed in inbox settings
 */
const showFollowupMode = computed(() => {
  return props.isFollowupAvailable;
});

/**
 * NOTICE: KAPTION-INTEGRATION
 * Get the current active tab index
 * Order: Reply/Follow-up (0), Schedule (1), Private Note (2)
 * When Schedule tab is not shown: Reply/Follow-up (0), Private Note (1)
 */
const activeTabIndex = computed(() => {
  if (props.disabled) {
    // Private Note is the fallback when replies are restricted, when permitted.
    if (!props.showPrivateNote) return 0;
    return props.showScheduleTab ? 2 : 1;
  }
  if (props.mode === REPLY_EDITOR_MODES.NOTE) {
    return props.showScheduleTab ? 2 : 1;
  }
  if (props.mode === REPLY_EDITOR_MODES.SCHEDULE) {
    return 1;
  }
  return 0; // REPLY or FOLLOWUP
});

/**
 * Computes the width of the sliding background chip in pixels
 * Includes 16px of padding in the calculation
 * @type {ComputedRef<string>}
 */
const width = computed(() => {
  let widthToUse = replyModeSize.width.value;

  if (props.showScheduleTab) {
    // Order: Reply/Follow-up, Schedule, Private Note
    if (activeTabIndex.value === 1) {
      widthToUse = scheduleModeSize.width.value;
    } else if (activeTabIndex.value === 2) {
      widthToUse = privateModeSize.width.value;
    }
  } else if (activeTabIndex.value === 1) {
    // Order: Reply/Follow-up, Private Note
    widthToUse = privateModeSize.width.value;
  }

  const widthWithPadding = widthToUse + 16;
  return `${widthWithPadding}px`;
});

/**
 * Computes the X translation value for the sliding background chip
 * @type {ComputedRef<string>}
 */
const translateValue = computed(() => {
  let xTranslate = 0;

  if (props.showScheduleTab) {
    // Order: Reply/Follow-up, Schedule, Private Note
    if (activeTabIndex.value === 1) {
      // Schedule tab - translate by Reply width + padding
      xTranslate = replyModeSize.width.value + 16;
    } else if (activeTabIndex.value === 2) {
      // Private Note tab - translate by Reply width + Schedule width + 2*padding
      xTranslate =
        replyModeSize.width.value + scheduleModeSize.width.value + 16 + 16;
    }
  } else if (activeTabIndex.value === 1) {
    // Order: Reply/Follow-up, Private Note
    xTranslate = replyModeSize.width.value + 16;
  }

  return `${xTranslate}px`;
});
</script>

<template>
  <div
    class="flex items-center w-auto h-8 p-1 transition-all rounded-full bg-n-alpha-2 group relative duration-300 ease-in-out z-0"
    :class="{
      'cursor-not-allowed': disabled,
    }"
  >
    <!-- NOTICE: KAPTION-INTEGRATION - Reply/Follow-up Tab -->
    <button
      ref="wootEditorReplyMode"
      class="flex items-center gap-1 px-2 z-20 cursor-pointer"
      :disabled="disabled"
      :class="{ 'cursor-not-allowed': disabled }"
      @click="
        !disabled &&
          $emit(
            'setMode',
            showFollowupMode
              ? REPLY_EDITOR_MODES.FOLLOWUP
              : REPLY_EDITOR_MODES.REPLY
          )
      "
    >
      <span
        :class="
          showFollowupMode
            ? 'i-lucide-message-square-reply'
            : 'i-ph-chat-circle'
        "
        class="text-sm"
      />
      <template v-if="showFollowupMode">
        {{ $t('CONVERSATION.REPLYBOX.FOLLOWUP', 'Follow-up') }}
      </template>
      <template v-else>
        {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
      </template>
    </button>

    <!-- NOTICE: KAPTION-INTEGRATION - Schedule Tab (only for WhatsApp/Twilio) - Before Private Note -->
    <button
      v-if="showScheduleTab"
      ref="wootEditorScheduleMode"
      class="flex items-center gap-1 px-2 z-20 cursor-pointer"
      @click="$emit('setMode', REPLY_EDITOR_MODES.SCHEDULE)"
    >
      <span class="i-lucide-calendar-clock text-sm" />
      {{ $t('CONVERSATION.REPLYBOX.SCHEDULE', 'Schedule') }}
    </button>

    <!-- NOTICE: KAPTION-INTEGRATION - Private Note Tab -->
    <button
      v-if="showPrivateNote"
      ref="wootEditorPrivateMode"
      class="flex items-center gap-1 px-2 z-20 cursor-pointer"
      @click="$emit('setMode', REPLY_EDITOR_MODES.NOTE)"
    >
      <span class="i-ph-note text-sm" />
      {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
    </button>

    <!-- Sliding background chip -->
    <div
      class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)] bg-n-solid-1"
      :class="{
        'transition-all duration-300': !disabled,
      }"
      :style="{
        '--chip-width': width,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </div>
</template>
